import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { verifyEmail, resendVerification } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function VerifyEmailPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [verifiedRole, setVerifiedRole] = useState(null);
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    // Check if the GET redirect already verified (query params from server)
    const urlStatus = searchParams.get('status');
    const urlRole = searchParams.get('role');

    // Helper: pull the Quick Post claim redirect (if any) that RegisterPage
    // stashed. Don't remove it here — CustomerDashboard's claim effect is
    // responsible for cleanup so the path survives an extra /login bounce.
    const peekRedirect = () => {
      try {
        const v = localStorage.getItem('postSignupRedirect');
        return v && v.startsWith('/customer') ? v : null;
      } catch {
        return null;
      }
    };

    if (urlStatus === 'success') {
      // The GET endpoint redirected us here after marking the email verified.
      // Always POST verifyEmail(token) — this is the idempotent handler that
      // also sets the auth cookies so checkAuth() can populate React state.
      setStatus('success');
      setMessage('Email verified successfully!');
      setVerifiedRole(urlRole || 'customer');
      (async () => {
        try { await verifyEmail(token); } catch { /* already verified is fine */ }
        try {
          await checkAuth();
          const defaultPath = urlRole === 'admin' ? '/admin' : urlRole === 'driver' ? '/driver' : '/customer';
          const target = peekRedirect() || defaultPath;
          setTimeout(() => navigate(target, { replace: true }), 2000);
        } catch { /* cookies blocked — user taps login button */ }
      })();
      return;
    }

    if (urlStatus === 'failed') {
      setStatus('error');
      setMessage('Verification link expired or invalid. Request a new one below.');
      return;
    }

    // No query params — user navigated directly or link didn't go through GET.
    // Fall back to POST verification.
    const verify = async () => {
      try {
        const res = await verifyEmail(token);
        const data = res.data;
        setMessage(data.message);
        setStatus('success');
        const role = data.role || 'customer';
        setVerifiedRole(role);

        if (data.auto_login || data.verified) {
          try {
            await checkAuth();
            const defaultPath = role === 'admin' ? '/admin' : role === 'driver' ? '/driver' : '/customer';
            const target = peekRedirect() || defaultPath;
            setTimeout(() => navigate(target, { replace: true }), 2000);
          } catch {
            // Cookies blocked — user taps login button
          }
        }
      } catch (err) {
        if (err.response) {
          const detail = err.response.data?.detail || '';
          setMessage(detail || 'Verification link expired or invalid. Request a new one below.');
        } else if (err.request) {
          setMessage('Unable to reach the server. Please check your internet connection and try again.');
        } else {
          setMessage('Something went wrong. Please try again or request a new verification link.');
        }
        setStatus('error');
      }
    };
    verify();
  }, [token, searchParams, navigate, checkAuth]);

  const handleResend = async () => {
    if (!resendEmail) return;
    setResending(true);
    setResendSuccess(false);
    try {
      await resendVerification(resendEmail);
      setResendSuccess(true);
    } catch (e) {}
    setResending(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-8" data-testid="verify-email-page">
      <div className="w-full max-w-md text-center">
        <div className="flex justify-center mb-8">
          <img src="/logo.png" alt="JustDeliver" className="h-12 w-auto" />
        </div>

        {status === 'loading' && (
          <div className="space-y-4">
            <Loader2 className="w-12 h-12 text-[#2E7D32] animate-spin mx-auto" />
            <p className="text-gray-600 text-lg">Verifying your email...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-6">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-[#2E7D32]" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Email Verified!</h2>
            <p className="text-gray-600">{message}</p>
            <p className="text-sm text-gray-500">Redirecting to your dashboard...</p>
            <Link to="/login">
              <Button className="w-full bg-[#2E7D32] text-white hover:bg-[#1B5E20]" data-testid="verified-login-btn">
                Log In to Dashboard
              </Button>
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-6">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Verification Failed</h2>
            <p className="text-gray-600">{message}</p>
            
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-3 text-left">
              <p className="text-sm font-medium text-gray-700">Request a new verification link:</p>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  className="pl-10 rounded-sm"
                  data-testid="resend-email-input"
                />
              </div>
              <Button
                onClick={handleResend}
                disabled={resending || !resendEmail}
                className="w-full bg-[#2E7D32] text-white hover:bg-[#1B5E20]"
                data-testid="resend-verification-btn"
              >
                {resending ? 'Sending...' : 'Resend Verification Email'}
              </Button>
              {resendSuccess && (
                <p className="text-sm text-green-700 text-center" data-testid="resend-success-msg">
                  Verification email sent! Check your inbox.
                </p>
              )}
            </div>

            <Link to="/login">
              <Button variant="outline" className="w-full" data-testid="verify-fail-login-btn">
                Back to Login
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
